import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { EntityModel } from '@data-layer/models/entities';
import { Subject } from 'rxjs';
import { DESC_ENTITY } from '@constants/desc-entity';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { enumIdentifierQualifier } from '@app/data-layer/enums/common';
import { EntityValidationHelper } from '@helpers/entity-validation.helper';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'desktop-entity-card',
  template: `
    <div
      class="desktop-entity-card desktop"
      [ngClass]="{ 'is-valid': entityIsValid, 'is-invalid': entityHasError, 'is-with-hover': !isShowEdit && isShowAddIcons }"
    >
      <div class="desktop-entity-card-header">
        <div class="desktop-entity-card-header-info">
          <span class="desktop-entity-is-valid" *ngIf="entityIsValid && isShowValidIcon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.0757 16.7728L6.17573 12.8728C5.94142 12.6385 5.94142 12.2586 6.17573 12.0243L7.02424 11.1757C7.25854 10.9414 7.63846 10.9414 7.87277 11.1757L10.5 13.8029L16.1272 8.17573C16.3615 7.94142 16.7415 7.94142 16.9758 8.17573L17.8243 9.02426C18.0586 9.25857 18.0586 9.63846 17.8243 9.87279L10.9243 16.7728C10.6899 17.0071 10.31 17.0071 10.0757 16.7728Z"
                fill="currentColor"
              />
            </svg>
          </span>

          <h3>{{ entityInfo?.titleDesktop | translate }}</h3>

          <app-hint-tooltip-icon *ngIf="entityInfo?.hint" [hint]="entityInfo.hint | translate"></app-hint-tooltip-icon>
        </div>

        <div class="desktop-entity-card-header-actions d-print-none d-flex align-items-center">
          <button
            type="button"
            class="btn btn-link"
            *ngIf="isImporter ? false : ((isShowEdit && !isManufacturer) || entities?.length === 1) && !isHideShowEdit"
            (click)="edit.emit(isManufacturer ? entities[0]?.id : entity?.id)"
          >
            <svg width="19" height="19" viewBox="0 0 19 19" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0 14.2525V18.0025H3.75L14.81 6.9425L11.06 3.1925L0 14.2525ZM17.71 4.0425C18.1 3.6525 18.1 3.0225 17.71 2.6325L15.37 0.2925C14.98 -0.0975 14.35 -0.0975 13.96 0.2925L12.13 2.1225L15.88 5.8725L17.71 4.0425Z"
                fill="currentColor"
              />
            </svg>

            <span class="ml-2">{{ 'BUTTON.EDIT' | translate }}</span>
          </button>

          <button
            type="button"
            class="btn btn-link ml-2"
            *ngIf="isManufacturer && entities?.length === 1"
            (click)="remove.emit(entities[0]?.id)"
          >
            <i class="fa fa-trash mr-2"></i>
            <span>{{ 'BUTTON.DELETE' | translate }}</span>
          </button>
        </div>

        <ng-content select="[entity-header-action]"></ng-content>
      </div>

      <div class="desktop-entity-card-body">
        <div class="desktop-entity-card-body-icon" [ngClass]="{ 'd-none d-lg-block': !!entity }">
          <img
            src="assets/icons/{{ entityInfo?.imgSrc + (!isShowEdit && isShowAddIcons && !isSkipAddIcon ? '_add' : '') }}.svg"
            alt="{{ entityInfo?.titleDesktop | translate }}"
          />
        </div>
        <div class="desktop-entity-card-body-data">
          <p *ngIf="(!isManufacturer || (isManufacturer && entities?.length === 0)) && !entity">
            {{ (entityInfo?.subTitleDesktop ? entityInfo?.subTitleDesktop : entityInfo?.subTitle) | translate }}
          </p>

          <p class="mb-2" *ngIf="!!descriptionText" [innerHTML]="descriptionText | translate"></p>

          <span class="text-hint" *ngIf="(!isShowEdit || isImporterFromCompany || entities?.length === 0) && isShowHint">
            {{ entityInfo?.subTitleHint | translate }}
          </span>
          <span class="text-hint warning" *ngIf="!isShowEdit && isCSOrLG">{{ 'INFO.CS_LS_ATTENTION_DESKTOP' | translate }}</span>

          <div class="desktop-entity-card-body-data-buttons d-print-none" *ngIf="!isShowEdit && !isImporter && !isHiddenButtons">
            <button type="button" class="btn btn-outline-secondary" [disabled]="isDisabledParent" (click)="useParent.emit()">
              {{ 'LABELS.USE_IMPORTER_DETAILS' | translate }}
            </button>

            <button type="button" class="btn btn-primary" (click)="edit.emit()">
              <i class="fa fa-plus-circle"></i>
              <span>{{ entityInfo?.addNew | translate }}</span>
            </button>
          </div>

          <ng-container *ngIf="!!entity">
            <ng-container *ngTemplateOutlet="entityItem; context: { entityItem: entity }"></ng-container>
          </ng-container>

          <ng-container *ngIf="entities?.length > 0">
            <ng-container
              *ngFor="let entityItemData of entities; let i = index"
              [ngTemplateOutlet]="entityItem"
              [ngTemplateOutletContext]="{ entityItem: entityItemData, index: i }"
            ></ng-container>
          </ng-container>
        </div>

        <div class="desktop-entity-card-body-add-hover" *ngIf="!isShowEdit && isShowAddIcons" (click)="useDifferent.emit()">
          <div class="desktop-entity-card-body-add-hover-icon">
            <svg width="40" height="40" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="13.5" cy="13.5" r="13.5" fill="#03BCBE" />
              <path d="M6 12H21V15H6V12Z" fill="white" />
              <path d="M12 21V6H15V21H12Z" fill="white" />
            </svg>
          </div>
          <span>{{ 'BUTTON.CLICK_TO_ADD' | translate }}</span>
        </div>
      </div>

      <div class="desktop-entity-card-footer">
        <div class="desktop-entity-card-footer-different-importer" *ngIf="isManufacturer && entities?.length > 0">
          <button type="button" class="btn btn-link" (click)="useDifferent.emit()">
            <i class="fa fa-plus-circle"></i>
            <span>{{ footerBtnText | translate }}</span>
          </button>
        </div>
      </div>

      <ng-content select="[entity-footer]"></ng-content>
    </div>

    <ng-template #entityItem let-entityItem="entityItem" let-index="index">
      <div class="desktop-entity-card-body-data-rows" *ngIf="isShowEdit">
        <div class="row" *ngIf="isManufacturer && entities?.length > 1">
          <div class="col-6 key">
            <h5>{{ 'MANUFACTURER' | translate }} #{{ index + 1 }}</h5>
          </div>
          <div class="col-6 value">
            <div class="d-flex justify-content-end">
              <button type="button" class="btn btn-link align-self-end mr-2" *ngIf="isShowEdit" (click)="edit.emit(entityItem?.id)">
                <svg width="19" height="19" viewBox="0 0 19 19" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M0 14.2525V18.0025H3.75L14.81 6.9425L11.06 3.1925L0 14.2525ZM17.71 4.0425C18.1 3.6525 18.1 3.0225 17.71 2.6325L15.37 0.2925C14.98 -0.0975 14.35 -0.0975 13.96 0.2925L12.13 2.1225L15.88 5.8725L17.71 4.0425Z"
                    fill="currentColor"
                  />
                </svg>

                <span class="ml-2">{{ 'BUTTON.EDIT' | translate }}</span>
              </button>

              <button type="button" class="btn btn-link align-self-end" *ngIf="isShowEdit" (click)="remove.emit(entityItem?.id)">
                <i class="fa fa-trash mr-2"></i>
                <span>{{ 'BUTTON.DELETE' | translate }}</span>
              </button>
            </div>
          </div>
        </div>
        <div class="row" *ngIf="isShowIdentifier">
          <div class="col-6 col-lg-2 key">{{ 'LABELS.ID_NUMBER' | translate }}</div>
          <ng-container *ngIf="!getValidationText(entityItem, 'entityIdentifier'); else missedIdentifier">
            <div class="col-6 col-lg-10 value">
              <span *ngIf="entityItem?.entityIdentifierQualifier">
                {{ 'IDENTIFIER_QUALIFIER.' + entityItem.entityIdentifierQualifier | translate }}
              </span>
              <span *ngIf="entityItem?.entityIdentifier">{{ entityItem.entityIdentifier | identifier }}</span>
            </div>
          </ng-container>
          <ng-template #missedIdentifier>
            <div class="col-6 col-lg-10 value text-error">
              {{
                getValidationText(entityItem, 'entityIdentifier') || getValidationText(entityItem, 'entityIdentifierQualifier') | translate
              }}
            </div>
          </ng-template>
        </div>
        <div
          class="row"
          *ngIf="isShowBirthDate && (entityItem?.dateOfBirth || entityItem?.entityIdentifierQualifier === enumIdentifierQualifier.SSN)"
        >
          <div class="col-6 col-lg-2 key">{{ 'FORM_LABEL.DATEOFBIRTH' | translate }}</div>

          <ng-container *ngIf="!getValidationText(entityItem, 'dateOfBirth'); else missedDateOfBirth">
            <div class="col-6 col-lg-10 value">{{ entityItem.dateOfBirth | customDateByUtc }}</div>
          </ng-container>
          <ng-template #missedDateOfBirth>
            <div class="col-6 col-lg-10 value text-error">{{ getValidationText(entityItem, 'dateOfBirth') | translate }}</div>
          </ng-template>
        </div>

        <div class="row">
          <div class="col-6 col-lg-2 key">{{ 'LABELS.NAME' | translate }}</div>

          <ng-container *ngIf="!getValidationText(entityItem, 'name'); else missedName">
            <div class="col-6 col-lg-10 value">{{ entityItem?.name }}</div>
          </ng-container>
          <ng-template #missedName>
            <div class="col-6 col-lg-10 value text-error">{{ getValidationText(entityItem, 'name') | translate }}</div>
          </ng-template>
        </div>

        <div class="row">
          <div class="col-6 col-lg-2 key">{{ 'LABELS.ADDRESS' | translate }}</div>

          <ng-container *ngIf="isValidIsfEntityAddress(entityItem); else missedAdd">
            <div class="col-6 col-lg-10 value" *ngIf="!getValidationText(entityItem, 'address')">
              <address-details [address]="entityItem?.address"></address-details>
            </div>
            <div class="col-6 col-lg-10 value text-error" *ngIf="getValidationText(entityItem, 'address')">
              {{ getValidationText(entityItem, 'address') | translate }}
            </div>
          </ng-container>
          <ng-template #missedAdd>
            <div class="col-6 col-lg-10 value text-error">{{ 'BACK_ERROR.MISSING_ADDRESS_PARTS' | translate }}</div>
          </ng-template>
        </div>

        <div class="row" *ngIf="isShowEmail">
          <div class="col-6 col-lg-2 key">{{ 'FORM_LABEL.EMAIL_SHORT' | translate }}</div>

          <ng-container *ngIf="!getValidationText(entityItem, 'email'); else missedEmail">
            <div class="col-6 col-lg-10 value value-wrap">
              <a href="mailto:{{ entityItem?.email }}" target="_blank">{{ entityItem?.email }}</a>
            </div>
          </ng-container>
          <ng-template #missedEmail>
            <div class="col-6 col-lg-10 value text-error">{{ getValidationText(entityItem, 'email') | translate }}</div>
          </ng-template>
        </div>

        <div class="row" *ngIf="isShowPhone">
          <div class="col-6 col-lg-2 key">{{ 'LABELS.MOBILE_NUMBER' | translate }}</div>

          <ng-container *ngIf="!getValidationText(entityItem, 'phone'); else missedPhone">
            <div class="col-6 col-lg-10 value">
              {{ entityItem?.phone }}
            </div>
          </ng-container>
          <ng-template #missedPhone>
            <div class="col-6 col-lg-10 value text-error">{{ getValidationText(entityItem, 'phone') | translate }}</div>
          </ng-template>
        </div>

        <div class="row" *ngIf="!!sameAs">
          <div class="col-12">
            <div class="entity-is-same-as">
              <i>=</i>
              {{ sameAs }}
            </div>
          </div>
        </div>
      </div>
    </ng-template>
  `,
  styleUrls: ['./desktop-entity-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopEntityCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public entity: EntityModel;
  @Input() public entities: EntityModel[];
  @Input() public entityCode: enumEntityModelCode = enumEntityModelCode.IMPORTER;
  @Input() public isImporterFromCompany: boolean;
  @Input() public isDisabledParent: boolean;
  @Input() public isHiddenButtons: boolean;
  @Input() public isShowAddIcons: boolean;
  @Input() public isSkipAddIcon: boolean;
  @Input() public isShowIdentifier: boolean = true;
  @Input() public isShowBirthDate: boolean = true;
  @Input() public isShowHint: boolean = true;
  @Input() public isShowValidIcon: boolean = true;
  @Input() public isHideShowEdit: boolean;
  @Input() public isShowEntityRequired: boolean;
  @Input() public descriptionText: string;
  @Input() public descEntityOverride: any = null;
  @Input() public sameAs: string;
  @Input() public isShowPhone: boolean;
  @Input() public isShowEmail: boolean;

  @Output() public edit: EventEmitter<number> = new EventEmitter<number>();
  @Output() public useParent: EventEmitter<void> = new EventEmitter<void>();
  @Output() public useDifferent: EventEmitter<void> = new EventEmitter<void>();
  @Output() public remove: EventEmitter<number> = new EventEmitter<number>();

  // entity of DESC_ENTITY object
  public entityInfo: {
    CODE: enumEntityModelCode;
    imgSrc: string;
    titleDesktop: string;
    subTitle: string;
    subTitleDesktop: string;
    hint: string;
    addDetails: string;
    tooltip: string;
    addNew: string;
    subTitleHint: string;
  } = null;
  public entityIsValid: boolean;
  public entityHasError: boolean;
  public isShowEdit: boolean;
  public isManufacturer: boolean;
  public isCSOrLG: boolean;
  public footerBtnText: string;

  public enumIdentifierQualifier = enumIdentifierQualifier;
  public enumEntityModelCode = enumEntityModelCode;
  public isIsf: boolean;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private route: ActivatedRoute) {}

  ngOnInit(): void {
    this.isIsf = this.route?.parent?.snapshot?.routeConfig?.path === 'isf' || location.pathname.includes('isf/batch');
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.entity) {
      this.getEntityHeaderData();
      this.getIsValid();
      this.getHasError();
      this.getIsShowEdit();
      this.getIsCSOrLG();
      this.setFooterBtnText();
    }
    if (changes?.entities) {
      this.getEntityHeaderData();
      this.getAreValid();
      this.getHasError();
      this.getIsManufacturer();
      this.getIsShowEdit();
      this.setFooterBtnText();
    }

    if (changes?.isShowEntityRequired) {
      this.getHasError();
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  public getValidationText(entity: EntityModel, fieldName: string): string {
    return EntityValidationHelper.getEntityValidationText(entity, fieldName);
  }

  public isValidIsfEntityAddress(entity: EntityModel): boolean {
    return EntityValidationHelper.isValidEntityAddress(entity?.address);
  }

  public setFooterBtnText(): void {
    this.footerBtnText = this.isManufacturer ? 'INFO.MF.ADD_NEW_DESKTOP' : 'INFO.IM.ADD_NEW_DESKTOP';
  }

  public get isImporter(): boolean {
    return this.entityCode === enumEntityModelCode.IMPORTER;
  }

  private getEntityHeaderData(): void {
    this.entityInfo = !!this.descEntityOverride ? this.descEntityOverride[this.entityCode] : DESC_ENTITY[this.entityCode];
  }

  private getIsValid(): void {
    this.entityIsValid = !!this.entity && !this.entity?.validationMessages?.length && this.isValidIsfEntityAddress(this.entity);
  }

  private getAreValid(): void {
    this.entityIsValid =
      this.entities?.length && this.entities.every((item) => !item.validationMessages?.length && this.isValidIsfEntityAddress(item));
  }

  private getHasError(): void {
    this.entityHasError =
      (!!this.entity && (this.entity?.validationMessages?.length > 0 || !this.isValidIsfEntityAddress(this.entity))) ||
      this.isShowEntityRequired;
  }

  private getIsShowEdit(): void {
    this.isShowEdit = this.isManufacturer ? this.entities?.length > 0 : !!this.entity;
  }

  private getIsManufacturer(): void {
    this.isManufacturer = this.entityCode === enumEntityModelCode.MANUFACTURER;
  }

  private getIsCSOrLG(): void {
    this.isCSOrLG = [enumEntityModelCode.CONSOLIDATOR, enumEntityModelCode.STAFFING_LOCATION].includes(this.entityCode);
  }
}
